import { StrictMode } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";

import { createRoot } from "react-dom/client";

import { LodgeApiError } from "./api/client";
import { AuthGate } from "./auth/AuthGate";
import { AuthProvider } from "./auth/AuthProvider";
import { routeTree } from "./routeTree.gen";

import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "@fontsource/inter/900.css";
import "./styles/global.scss";

// A 4xx is an answer, not a hiccup. Left to the default three retries with
// backoff, a record somebody deleted spends about seven seconds saying
// "Loading…" and asks three more times before the page can say it is gone -
// and a 400 from a filter this build sends wrong is retried just as
// pointlessly. Anything 5xx or a lost connection still retries.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof LodgeApiError && error.status >= 400 && error.status < 500) {
          return false;
        }

        return failureCount < 3;
      },
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPendingMs: 200,
  defaultPendingMinMs: 300,
  context: { queryClient },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthGate>
          <RouterProvider router={router} />
        </AuthGate>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
